"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Scroll-triggered reveal hook.
 *
 * Returns a ref and a className that applies the `.reveal` (or
 * `.reveal-stagger`) base + adds `.is-visible` once the element enters
 * the viewport. CSS handles the opacity + slide transitions.
 *
 * Usage:
 *   const { ref, className } = useReveal({ stagger: true });
 *   <div ref={ref} className={`grid ... ${className}`}>...children...</div>
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>({
  stagger = false,
  threshold = 0.15,
  rootMargin = "0px 0px -10% 0px",
}: {
  stagger?: boolean;
  threshold?: number;
  rootMargin?: string;
} = {}) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  const base = stagger ? "reveal-stagger" : "reveal";
  const className = `${base}${visible ? " is-visible" : ""}`;
  return { ref, className };
}
